/**
 * sceneCounts — what the Pixi scene holds right now (plan 13 §8).
 *
 * A leak in a renderer shows up as a count that only grows: display objects
 * never removed, Graphics never destroyed, textures re-created per hit. The
 * frame-budget monitor attaches these counts to every slow-window report and
 * scripts/qa/perf-scenario-probe.mjs samples them once a second, so a slope is
 * visible after the fact.
 *
 * Reads the renderer through PixiOverlayHost.getRenderer(); the stage is the
 * renderer's `lastObjectRendered` (Pixi 8). Cost: one walk of the display tree,
 * only when a report is built.
 */

import type { FrameBudgetSceneCounts } from '../../shared/diagnostics/FrameBudgetTypes';
import { PixiOverlayHost } from '../rendering/PixiOverlayHost';

interface DisplayNode {
  renderPipeId?: string;
  children?: readonly DisplayNode[];
}

interface RendererView {
  lastObjectRendered?: DisplayNode;
  texture?: { managedTextures?: readonly unknown[] };
  /** A GCManagedHash: `items` keyed by uid, released entries set to null. */
  graphicsContext?: { _managedContexts?: { items?: Record<string, unknown> } };
}

/** Count a display tree by Pixi render pipe (`graphics`, `sprite`, `text`, …). */
export function countDisplayTree(root: DisplayNode): Pick<FrameBudgetSceneCounts, 'displayObjects' | 'graphics' | 'sprites' | 'text'> {
  let displayObjects = 0;
  let graphics = 0;
  let sprites = 0;
  let text = 0;
  const stack: DisplayNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    displayObjects++;
    const pipe = node.renderPipeId;
    if (pipe === 'graphics') graphics++;
    else if (pipe === 'sprite' || pipe === 'nineSliceSprite' || pipe === 'tilingSprite') sprites++;
    else if (pipe === 'text' || pipe === 'bitmapText' || pipe === 'htmlText') text++;
    const kids = node.children;
    if (kids) for (let i = 0; i < kids.length; i++) stack.push(kids[i]);
  }
  return { displayObjects, graphics, sprites, text };
}

export function readSceneCounts(): FrameBudgetSceneCounts | null {
  const renderer = PixiOverlayHost.getRenderer() as unknown as RendererView | null;
  const stage = renderer?.lastObjectRendered;
  if (!renderer || !stage) return null;
  const managed = renderer.texture?.managedTextures;
  const contexts = renderer.graphicsContext?._managedContexts?.items;
  return {
    ...countDisplayTree(stage),
    // managedTextures keeps null holes where a texture was released.
    gpuTextures: managed ? managed.filter(Boolean).length : null,
    graphicsContexts: contexts ? Object.values(contexts).filter(Boolean).length : null,
  };
}
